import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { loadStoreProducts } from '../services/AdminMenuService.ts';
import { MenuProduct, MenuService } from '../services/MenuService.ts';
import {
  getCollectionsForAdmin,
  createCollection,
  updateCollection,
  deleteCollection,
  getAllScheduledCollections,
  createScheduledCollection,
  updateScheduledCollection,
  deleteScheduledCollection,
  reorderUnified,
  CollectionItem,
  ScheduledCollectionData,
} from '../services/homeCollectionsApiService.ts';
import {
  combineDateTimeInputs,
  convertUtcToStoreTimezone,
  formatDateRange,
  getDateInputValue,
  getTimeInputValue,
  getTimezoneAbbreviation,
  isBrowserTimzoneDifferent,
} from '../utils/timezoneUtils.ts';
import ProductPicker from './ProductPicker.tsx';
import './HomeCollectionsEditor.css';

interface HomeCollectionsEditorProps {
  storeName: string;
  menuType: 'med' | 'rec';
  displayName: string;
  retailerId: string;
  legacyMenuId?: string;
  onBack: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const format12h = (time24: string): string => {
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
};

const HomeCollectionsEditor: React.FC<HomeCollectionsEditorProps> = ({
  storeName,
  menuType,
  displayName,
  retailerId,
  legacyMenuId,
  onBack,
}) => {
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [scheduledCollections, setScheduledCollections] = useState<ScheduledCollectionData[]>([]);
  const [allProducts, setAllProducts] = useState<MenuProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [createScheduled, setCreateScheduled] = useState(false);

  // Editing - always-on
  const [expandedCollectionId, setExpandedCollectionId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Editing - scheduled
  const [expandedScheduledId, setExpandedScheduledId] = useState<string | null>(null);

  // Scheduling fields (shared between create form and edit views)
  const [isScheduledToggle, setIsScheduledToggle] = useState(false);
  const [schedName, setSchedName] = useState('');
  const [schedProductIds, setSchedProductIds] = useState<string[]>([]);
  const [schedStartDate, setSchedStartDate] = useState('');
  const [schedStartTime, setSchedStartTime] = useState('12:00');
  const [schedStartAmPm, setSchedStartAmPm] = useState<'AM' | 'PM'>('AM');
  const [schedEndDate, setSchedEndDate] = useState('');
  const [schedEndTime, setSchedEndTime] = useState('12:00');
  const [schedEndAmPm, setSchedEndAmPm] = useState<'AM' | 'PM'>('AM');
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedShuffleProducts, setSchedShuffleProducts] = useState(false);

  // Recurring schedule
  const [createRecurring, setCreateRecurring] = useState(false);
  const [recurringStartDay, setRecurringStartDay] = useState(5); // Friday
  const [recurringStartTime, setRecurringStartTime] = useState('12:00');
  const [recurringStartAmPm, setRecurringStartAmPm] = useState<'AM' | 'PM'>('AM');
  const [recurringEndDay, setRecurringEndDay] = useState(5);
  const [recurringEndTime, setRecurringEndTime] = useState('12:00');
  const [recurringEndAmPm, setRecurringEndAmPm] = useState<'AM' | 'PM'>('AM');

  // Deal import
  const [availableDeals, setAvailableDeals] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [importingDeal, setImportingDeal] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  // Product map for fast lookup
  const productMap = React.useMemo(() => {
    const map = new Map<string, MenuProduct>();
    allProducts.forEach(p => map.set(p.id, p));
    return map;
  }, [allProducts]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [config, products, scheduled] = await Promise.all([
        getCollectionsForAdmin(storeName, menuType),
        loadStoreProducts(retailerId),
        getAllScheduledCollections(storeName, menuType).catch(() => []),
      ]);
      setCollections(config?.collections || []);
      setAllProducts(products);
      setScheduledCollections(scheduled);
    } catch (error) {
      console.error('Error loading data:', error);
      alert(`Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [storeName, menuType, retailerId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ==========================================
  // Schedule field helpers
  // ==========================================

  const resetScheduleFields = () => {
    setIsScheduledToggle(false);
    setSchedName('');
    setSchedProductIds([]);
    setSchedStartDate('');
    setSchedStartTime('12:00');
    setSchedStartAmPm('AM');
    setSchedEndDate('');
    setSchedEndTime('12:00');
    setSchedEndAmPm('AM');
    setCreateRecurring(false);
    setRecurringStartDay(5);
    setRecurringStartTime('12:00');
    setRecurringStartAmPm('AM');
    setRecurringEndDay(5);
    setRecurringEndTime('12:00');
    setRecurringEndAmPm('AM');
    setSchedShuffleProducts(false);
  };

  const populateScheduleFields = (col: ScheduledCollectionData) => {
    setSchedName(col.name);
    setSchedProductIds([...col.productIds]);
    setSchedShuffleProducts(col.shuffleProducts ?? false);

    if (col.isRecurring) {
      setCreateRecurring(true);
      setIsScheduledToggle(false);
      if (col.recurringStartDay !== undefined) setRecurringStartDay(col.recurringStartDay);
      if (col.recurringStartTime) {
        const [h, m] = col.recurringStartTime.split(':').map(Number);
        const isPm = h >= 12;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        setRecurringStartTime(`${hour12}:${m.toString().padStart(2, '0')}`);
        setRecurringStartAmPm(isPm ? 'PM' : 'AM');
      }
      if (col.recurringEndDay !== undefined) setRecurringEndDay(col.recurringEndDay);
      if (col.recurringEndTime) {
        const [h, m] = col.recurringEndTime.split(':').map(Number);
        const isPm = h >= 12;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        setRecurringEndTime(`${hour12}:${m.toString().padStart(2, '0')}`);
        setRecurringEndAmPm(isPm ? 'PM' : 'AM');
      }
    } else {
      setCreateRecurring(false);
      setIsScheduledToggle(true);
      if (col.startDateTime) {
        const startInStore = convertUtcToStoreTimezone(col.startDateTime, storeName);
        setSchedStartDate(getDateInputValue(startInStore, storeName));
        const startTimeStr = getTimeInputValue(startInStore, storeName);
        const [h, m] = startTimeStr.split(':').map(Number);
        const isPm = h >= 12;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        setSchedStartTime(`${hour12}:${m.toString().padStart(2, '0')}`);
        setSchedStartAmPm(isPm ? 'PM' : 'AM');
      }
      if (col.endDateTime) {
        const endInStore = convertUtcToStoreTimezone(col.endDateTime, storeName);
        setSchedEndDate(getDateInputValue(endInStore, storeName));
        const endTimeStr = getTimeInputValue(endInStore, storeName);
        const [h, m] = endTimeStr.split(':').map(Number);
        const isPm = h >= 12;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        setSchedEndTime(`${hour12}:${m.toString().padStart(2, '0')}`);
        setSchedEndAmPm(isPm ? 'PM' : 'AM');
      }
    }
  };

  const combineDateTime = (date: string, time: string, ampm: 'AM' | 'PM'): Date | null => {
    if (!date || !time) return null;
    const match = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return null;
    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    if (ampm === 'PM' && hour < 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    const timeStr24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    return combineDateTimeInputs(date, timeStr24, storeName);
  };

  const normalizeTimeInput = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '12:00';
    // Already valid HH:MM format
    if (/^(0?[1-9]|1[0-2]):[0-5][0-9]$/.test(trimmed)) return trimmed;
    // Just a number (e.g. "9" → "9:00", "11" → "11:00")
    const hourOnly = trimmed.match(/^(\d{1,2})$/);
    if (hourOnly) {
      const h = parseInt(hourOnly[1], 10);
      if (h >= 1 && h <= 12) return `${h}:00`;
    }
    // Number with colon but no minutes (e.g. "9:" → "9:00")
    const hourColon = trimmed.match(/^(\d{1,2}):$/);
    if (hourColon) {
      const h = parseInt(hourColon[1], 10);
      if (h >= 1 && h <= 12) return `${h}:00`;
    }
    // Invalid — reset to 12:00
    return '12:00';
  };

  const to24h = (time: string, ampm: 'AM' | 'PM'): string => {
    const match = time.match(/^(\d{1,2}):(\d{2})$/);
    if (!match) return '00:00';
    let hour = parseInt(match[1], 10);
    const minute = parseInt(match[2], 10);
    if (ampm === 'PM' && hour < 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // ==========================================
  // Deal import
  // ==========================================

  const fetchDeals = async () => {
    if (!legacyMenuId || availableDeals.length > 0) return;
    setDealsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals/${legacyMenuId}/all`);
      if (!response.ok) { setDealsLoading(false); return; }
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setAvailableDeals(data.data);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setDealsLoading(false);
    }
  };

  const handleImportDealProducts = async (col: CollectionItem) => {
    if (!legacyMenuId || !selectedDealId) return;
    setImportingDeal(true);
    setImportResult(null);
    try {
      const products = await MenuService.getProductsForSpecial(legacyMenuId, selectedDealId);
      const productIds = products.map(p => p.id);
      if (productIds.length === 0) {
        setImportResult('No products found in this deal.');
        return;
      }
      const newIds = productIds.filter(id => !col.productIds.includes(id));
      if (newIds.length === 0) {
        setImportResult('All products from this deal are already in the collection.');
        return;
      }
      await handleAddMultipleProducts(col, newIds);
      const dealName = availableDeals.find(d => d.id === selectedDealId)?.name || 'deal';
      setImportResult(`Added ${newIds.length} product${newIds.length !== 1 ? 's' : ''} from "${dealName}"`);
      setSelectedDealId('');
    } catch (error) {
      setImportResult(`Failed to import: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setImportingDeal(false);
    }
  };

  const handleImportDealProductsScheduled = async () => {
    if (!legacyMenuId || !selectedDealId) return;
    setImportingDeal(true);
    setImportResult(null);
    try {
      const products = await MenuService.getProductsForSpecial(legacyMenuId, selectedDealId);
      const productIds = products.map(p => p.id);
      if (productIds.length === 0) {
        setImportResult('No products found in this deal.');
        return;
      }
      const newIds = productIds.filter(id => !schedProductIds.includes(id));
      if (newIds.length === 0) {
        setImportResult('All products from this deal are already in the collection.');
        return;
      }
      setSchedProductIds(prev => [...prev, ...newIds]);
      const dealName = availableDeals.find(d => d.id === selectedDealId)?.name || 'deal';
      setImportResult(`Added ${newIds.length} product${newIds.length !== 1 ? 's' : ''} from "${dealName}"`);
      setSelectedDealId('');
    } catch (error) {
      setImportResult(`Failed to import: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setImportingDeal(false);
    }
  };

  // ==========================================
  // Expand / collapse
  // ==========================================

  const handleExpandAlwaysOn = (colId: string | null) => {
    if (expandedScheduledId) {
      setExpandedScheduledId(null);
      resetScheduleFields();
    }
    setExpandedCollectionId(colId === expandedCollectionId ? null : colId);
    setIsScheduledToggle(false);
    setCreateRecurring(false);
    setSchedStartDate('');
    setSchedStartTime('12:00');
    setSchedStartAmPm('AM');
    setSchedEndDate('');
    setSchedEndTime('12:00');
    setSchedEndAmPm('AM');
  };

  const handleExpandScheduled = (col: ScheduledCollectionData | null) => {
    if (expandedCollectionId) {
      setExpandedCollectionId(null);
    }
    if (!col || expandedScheduledId === col.documentId) {
      setExpandedScheduledId(null);
      resetScheduleFields();
      return;
    }
    setExpandedScheduledId(col.documentId);
    populateScheduleFields(col);
  };

  // ==========================================
  // Always-on collection handlers
  // ==========================================

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      if (createScheduled || createRecurring) {
        if (createRecurring) {
          // Create as recurring
          await createScheduledCollection(storeName, menuType, {
            name: newName.trim(),
            productIds: schedProductIds,
            isRecurring: true,
            recurringStartDay: recurringStartDay,
            recurringStartTime: to24h(recurringStartTime, recurringStartAmPm),
            recurringEndDay: recurringEndDay,
            recurringEndTime: to24h(recurringEndTime, recurringEndAmPm),
            shuffleProducts: schedShuffleProducts,
          });
        } else {
          // Create as scheduled (fixed date range)
          const startDt = combineDateTime(schedStartDate, schedStartTime, schedStartAmPm);
          const endDt = combineDateTime(schedEndDate, schedEndTime, schedEndAmPm);
          if (!startDt || !endDt) { alert('Please enter valid start and end dates.'); setSaving(false); return; }
          if (startDt >= endDt) { alert('Start must be before end.'); setSaving(false); return; }
          await createScheduledCollection(storeName, menuType, {
            name: newName.trim(),
            productIds: schedProductIds,
            startDateTime: startDt,
            endDateTime: endDt,
            shuffleProducts: schedShuffleProducts,
          });
        }
        const refreshed = await getAllScheduledCollections(storeName, menuType);
        setScheduledCollections(refreshed);
      } else {
        // Create as always-on
        const collectionId = await createCollection(storeName, menuType, newName.trim(), schedProductIds);
        setCollections(prev => [
          ...prev,
          {
            id: collectionId,
            name: newName.trim(),
            productIds: schedProductIds,
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ]);
        setExpandedCollectionId(collectionId);
      }
      setNewName('');
      setShowCreateForm(false);
      setCreateScheduled(false);
      setCreateRecurring(false);
      setSchedProductIds([]);
      resetScheduleFields();
    } catch (error) {
      alert(`Failed to create collection: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (col: CollectionItem) => {
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { isActive: !col.isActive });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, isActive: !c.isActive } : c))
      );
    } catch (error) {
      alert(`Failed to toggle: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleShuffle = async (col: CollectionItem) => {
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { shuffleProducts: !col.shuffleProducts });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, shuffleProducts: !c.shuffleProducts } : c))
      );
    } catch (error) {
      alert(`Failed to toggle shuffle: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (col: CollectionItem) => {
    if (!renameValue.trim() || renameValue.trim() === col.name) {
      setRenamingId(null);
      return;
    }
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { name: renameValue.trim() });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, name: renameValue.trim() } : c))
      );
      setRenamingId(null);
    } catch (error) {
      alert(`Failed to rename: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (col: CollectionItem) => {
    if (!window.confirm(`Delete "${col.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await deleteCollection(storeName, menuType, col.id);
      setCollections(prev => prev.filter(c => c.id !== col.id));
      if (expandedCollectionId === col.id) setExpandedCollectionId(null);
    } catch (error) {
      alert(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddProduct = async (col: CollectionItem, productId: string) => {
    if (col.productIds.includes(productId)) return;
    const newProductIds = [...col.productIds, productId];
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { productIds: newProductIds });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, productIds: newProductIds } : c))
      );
    } catch (error) {
      alert(`Failed to add product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProduct = async (col: CollectionItem, productId: string) => {
    const newProductIds = col.productIds.filter(id => id !== productId);
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { productIds: newProductIds });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, productIds: newProductIds } : c))
      );
    } catch (error) {
      alert(`Failed to remove product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMultipleProducts = async (col: CollectionItem, productIds: string[]) => {
    const newProductIds = col.productIds.filter(id => !productIds.includes(id));
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { productIds: newProductIds });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, productIds: newProductIds } : c))
      );
    } catch (error) {
      alert(`Failed to remove products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddMultipleProducts = async (col: CollectionItem, productIds: string[]) => {
    const uniqueNewIds = productIds.filter(id => !col.productIds.includes(id));
    if (uniqueNewIds.length === 0) return;
    const newProductIds = [...col.productIds, ...uniqueNewIds];
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { productIds: newProductIds });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, productIds: newProductIds } : c))
      );
    } catch (error) {
      alert(`Failed to add products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleClearAllProducts = async (col: CollectionItem) => {
    if (!window.confirm(`Remove all ${col.productIds.length} products from "${col.name}"?`)) return;
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { productIds: [] });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, productIds: [] } : c))
      );
    } catch (error) {
      alert(`Failed to clear products: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReorderProducts = async (col: CollectionItem, reorderedIds: string[]) => {
    setSaving(true);
    try {
      await updateCollection(storeName, menuType, col.id, { productIds: reorderedIds });
      setCollections(prev =>
        prev.map(c => (c.id === col.id ? { ...c, productIds: reorderedIds } : c))
      );
    } catch (error) {
      alert(`Failed to reorder: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Convert always-on → scheduled or recurring
  const handleConvertToScheduled = async (col: CollectionItem) => {
    if (createRecurring) {
      if (!window.confirm(`Convert "${col.name}" to a recurring collection?`)) return;
      setSaving(true);
      try {
        await createScheduledCollection(storeName, menuType, {
          name: col.name,
          productIds: col.productIds,
          isRecurring: true,
          recurringStartDay: recurringStartDay,
          recurringStartTime: to24h(recurringStartTime, recurringStartAmPm),
          recurringEndDay: recurringEndDay,
          recurringEndTime: to24h(recurringEndTime, recurringEndAmPm),
        });
        await deleteCollection(storeName, menuType, col.id);
        const [config, scheduled] = await Promise.all([
          getCollectionsForAdmin(storeName, menuType),
          getAllScheduledCollections(storeName, menuType),
        ]);
        setCollections(config?.collections || []);
        setScheduledCollections(scheduled);
        setExpandedCollectionId(null);
        resetScheduleFields();
      } catch (error) {
        alert(`Failed to convert: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setSaving(false);
      }
      return;
    }

    const startDt = combineDateTime(schedStartDate, schedStartTime, schedStartAmPm);
    const endDt = combineDateTime(schedEndDate, schedEndTime, schedEndAmPm);
    if (!startDt || !endDt) { alert('Please enter valid start and end dates.'); return; }
    if (startDt >= endDt) { alert('Start must be before end.'); return; }
    if (!window.confirm(`Convert "${col.name}" to a scheduled collection? It will only appear during the specified time window.`)) return;

    setSaving(true);
    try {
      await createScheduledCollection(storeName, menuType, {
        name: col.name,
        productIds: col.productIds,
        startDateTime: startDt,
        endDateTime: endDt,
      });
      await deleteCollection(storeName, menuType, col.id);
      const [config, scheduled] = await Promise.all([
        getCollectionsForAdmin(storeName, menuType),
        getAllScheduledCollections(storeName, menuType),
      ]);
      setCollections(config?.collections || []);
      setScheduledCollections(scheduled);
      setExpandedCollectionId(null);
      resetScheduleFields();
    } catch (error) {
      alert(`Failed to convert: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // Scheduled collection handlers
  // ==========================================

  const handleSaveScheduledEdit = async () => {
    if (!expandedScheduledId) return;
    if (!schedName.trim()) { alert('Collection name is required.'); return; }

    setSchedSaving(true);
    try {
      if (createRecurring) {
        // Save as recurring
        await updateScheduledCollection(storeName, menuType, expandedScheduledId, {
          name: schedName.trim(),
          productIds: schedProductIds,
          isRecurring: true,
          recurringStartDay: recurringStartDay,
          recurringStartTime: to24h(recurringStartTime, recurringStartAmPm),
          recurringEndDay: recurringEndDay,
          recurringEndTime: to24h(recurringEndTime, recurringEndAmPm),
          shuffleProducts: schedShuffleProducts,
        });
      } else {
        // Save as scheduled (fixed date range)
        const startDt = combineDateTime(schedStartDate, schedStartTime, schedStartAmPm);
        const endDt = combineDateTime(schedEndDate, schedEndTime, schedEndAmPm);
        if (!startDt || !endDt) { alert('Please enter valid start and end dates.'); setSchedSaving(false); return; }
        if (startDt >= endDt) { alert('Start must be before end.'); setSchedSaving(false); return; }
        await updateScheduledCollection(storeName, menuType, expandedScheduledId, {
          name: schedName.trim(),
          productIds: schedProductIds,
          isRecurring: false,
          startDateTime: startDt,
          endDateTime: endDt,
          shuffleProducts: schedShuffleProducts,
        });
      }
      const refreshed = await getAllScheduledCollections(storeName, menuType);
      setScheduledCollections(refreshed);
      setExpandedScheduledId(null);
      resetScheduleFields();
    } catch (error) {
      alert(`Failed to update: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSchedSaving(false);
    }
  };

  const handleDeleteScheduled = async (col: ScheduledCollectionData) => {
    if (!window.confirm(`Delete "${col.name}"? This cannot be undone.`)) return;
    try {
      await deleteScheduledCollection(storeName, menuType, col.documentId);
      setScheduledCollections(prev => prev.filter(c => c.documentId !== col.documentId));
      if (expandedScheduledId === col.documentId) {
        setExpandedScheduledId(null);
        resetScheduleFields();
      }
    } catch (error) {
      alert(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleConvertToAlwaysOn = async (col: ScheduledCollectionData) => {
    if (!window.confirm(`Convert "${col.name}" to an always-on collection? It will appear permanently until deactivated.`)) return;
    setSaving(true);
    try {
      await createCollection(storeName, menuType, col.name, col.productIds);
      await deleteScheduledCollection(storeName, menuType, col.documentId);
      const [config, scheduled] = await Promise.all([
        getCollectionsForAdmin(storeName, menuType),
        getAllScheduledCollections(storeName, menuType),
      ]);
      setCollections(config?.collections || []);
      setScheduledCollections(scheduled);
      setExpandedScheduledId(null);
      resetScheduleFields();
    } catch (error) {
      alert(`Failed to convert: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateScheduled = (col: ScheduledCollectionData) => {
    setShowCreateForm(true);
    setNewName(col.name);
    setSchedProductIds([...col.productIds]);
    setSchedShuffleProducts(col.shuffleProducts ?? false);

    if (col.isRecurring) {
      setCreateRecurring(true);
      setCreateScheduled(false);
      if (col.recurringStartDay !== undefined) setRecurringStartDay(col.recurringStartDay);
      if (col.recurringStartTime) {
        const [h, m] = col.recurringStartTime.split(':').map(Number);
        const isPm = h >= 12;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        setRecurringStartTime(`${hour12}:${m.toString().padStart(2, '0')}`);
        setRecurringStartAmPm(isPm ? 'PM' : 'AM');
      }
      if (col.recurringEndDay !== undefined) setRecurringEndDay(col.recurringEndDay);
      if (col.recurringEndTime) {
        const [h, m] = col.recurringEndTime.split(':').map(Number);
        const isPm = h >= 12;
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        setRecurringEndTime(`${hour12}:${m.toString().padStart(2, '0')}`);
        setRecurringEndAmPm(isPm ? 'PM' : 'AM');
      }
    } else {
      setCreateScheduled(true);
      setCreateRecurring(false);
      setSchedStartDate('');
      setSchedStartTime('12:00');
      setSchedStartAmPm('AM');
      setSchedEndDate('');
      setSchedEndTime('12:00');
      setSchedEndAmPm('AM');
    }
  };

  // ==========================================
  // Shared deal import UI renderer
  // ==========================================

  const renderDealImport = (onImport: () => void) => {
    if (!legacyMenuId) return null;
    return (
      <div className="hce-deal-import">
        <div className="hce-schedule-divider"><span>Import from Dutchie Deal</span></div>
        <div className="hce-deal-import-row">
          <select
            className="hce-deal-select"
            value={selectedDealId}
            onChange={e => { setSelectedDealId(e.target.value); setImportResult(null); }}
            onFocus={fetchDeals}
            disabled={importingDeal}
          >
            <option value="">
              {dealsLoading ? 'Loading deals...' : '-- Select a deal --'}
            </option>
            {availableDeals.map(deal => (
              <option key={deal.id} value={deal.id}>
                {deal.name} ({deal.type})
              </option>
            ))}
          </select>
          <button
            className="hce-import-btn"
            onClick={onImport}
            disabled={!selectedDealId || importingDeal}
          >
            {importingDeal ? 'Importing...' : 'Import Products'}
          </button>
        </div>
        {importResult && (
          <div className="hce-import-result">{importResult}</div>
        )}
      </div>
    );
  };

  // ==========================================
  // Shared scheduling UI renderer
  // ==========================================

  const renderSchedulingInputs = (disabled: boolean) => (
    <div className={`hce-scheduling-inputs${disabled ? ' disabled' : ''}`}>
      <div className="hce-datetime-group">
        <label>Start Date & Time ({getTimezoneAbbreviation(storeName)})</label>
        <div className="hce-datetime-inputs">
          <input
            type="date"
            value={schedStartDate}
            onChange={e => setSchedStartDate(e.target.value)}
            className="hce-date-input"
            min={new Date().toISOString().split('T')[0]}
            disabled={disabled}
          />
          <input
            type="text"
            value={schedStartTime}
            onChange={e => setSchedStartTime(e.target.value)}
            onBlur={e => setSchedStartTime(normalizeTimeInput(e.target.value))}
            className="hce-time-input"
            placeholder="12:00"
            pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
            maxLength={5}
            title="Enter time in 12-hour format (1:00 - 12:59)"
            disabled={disabled}
          />
          <select
            value={schedStartAmPm}
            onChange={e => setSchedStartAmPm(e.target.value as 'AM' | 'PM')}
            className="hce-ampm-select"
            disabled={disabled}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
      <div className="hce-datetime-group">
        <label>End Date & Time ({getTimezoneAbbreviation(storeName)})</label>
        <div className="hce-datetime-inputs">
          <input
            type="date"
            value={schedEndDate}
            onChange={e => setSchedEndDate(e.target.value)}
            className="hce-date-input"
            min={schedStartDate || new Date().toISOString().split('T')[0]}
            disabled={disabled}
          />
          <input
            type="text"
            value={schedEndTime}
            onChange={e => setSchedEndTime(e.target.value)}
            onBlur={e => setSchedEndTime(normalizeTimeInput(e.target.value))}
            className="hce-time-input"
            placeholder="12:00"
            pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
            maxLength={5}
            title="Enter time in 12-hour format (1:00 - 12:59)"
            disabled={disabled}
          />
          <select
            value={schedEndAmPm}
            onChange={e => setSchedEndAmPm(e.target.value as 'AM' | 'PM')}
            className="hce-ampm-select"
            disabled={disabled}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderRecurringInputs = (disabled: boolean) => (
    <div className={`hce-scheduling-inputs${disabled ? ' disabled' : ''}`}>
      <div className="hce-datetime-group">
        <label>Start Day & Time ({getTimezoneAbbreviation(storeName)})</label>
        <div className="hce-datetime-inputs">
          <select
            value={recurringStartDay}
            onChange={e => setRecurringStartDay(parseInt(e.target.value))}
            className="hce-day-select"
            disabled={disabled}
          >
            {DAY_NAMES.map((day, i) => (
              <option key={i} value={i}>{day}</option>
            ))}
          </select>
          <input
            type="text"
            value={recurringStartTime}
            onChange={e => setRecurringStartTime(e.target.value)}
            onBlur={e => setRecurringStartTime(normalizeTimeInput(e.target.value))}
            className="hce-time-input"
            placeholder="12:00"
            pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
            maxLength={5}
            disabled={disabled}
          />
          <select
            value={recurringStartAmPm}
            onChange={e => setRecurringStartAmPm(e.target.value as 'AM' | 'PM')}
            className="hce-ampm-select"
            disabled={disabled}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
      <div className="hce-datetime-group">
        <label>End Day & Time ({getTimezoneAbbreviation(storeName)})</label>
        <div className="hce-datetime-inputs">
          <select
            value={recurringEndDay}
            onChange={e => setRecurringEndDay(parseInt(e.target.value))}
            className="hce-day-select"
            disabled={disabled}
          >
            {DAY_NAMES.map((day, i) => (
              <option key={i} value={i}>{day}</option>
            ))}
          </select>
          <input
            type="text"
            value={recurringEndTime}
            onChange={e => setRecurringEndTime(e.target.value)}
            onBlur={e => setRecurringEndTime(normalizeTimeInput(e.target.value))}
            className="hce-time-input"
            placeholder="12:00"
            pattern="^(0?[1-9]|1[0-2]):[0-5][0-9]$"
            maxLength={5}
            disabled={disabled}
          />
          <select
            value={recurringEndAmPm}
            onChange={e => setRecurringEndAmPm(e.target.value as 'AM' | 'PM')}
            className="hce-ampm-select"
            disabled={disabled}
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>
    </div>
  );

  // ==========================================
  // Filtered scheduled lists
  // ==========================================

  const activeScheduled = scheduledCollections.filter(c => c.isRecurring || c.status === 'current' || c.status === 'scheduled');
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const expiredScheduled = scheduledCollections.filter(
    c => c.status === 'expired' && c.endDateTime && c.endDateTime >= thirtyDaysAgo
  );

  type UnifiedItem =
    | { type: 'always-on'; data: CollectionItem }
    | { type: 'scheduled'; data: ScheduledCollectionData };

  const unifiedList: UnifiedItem[] = (() => {
    const alwaysOnItems: UnifiedItem[] = collections.map((c, i) => ({
      type: 'always-on',
      data: { ...c, displayOrder: c.displayOrder ?? i },
    }));
    const scheduledItems: UnifiedItem[] = activeScheduled.map(c => ({
      type: 'scheduled',
      data: c,
    }));

    return [...alwaysOnItems, ...scheduledItems].sort((a, b) => {
      const orderA = a.type === 'always-on'
        ? (a.data as CollectionItem).displayOrder ?? 999
        : (a.data as ScheduledCollectionData).displayOrder ?? 999;
      const orderB = b.type === 'always-on'
        ? (b.data as CollectionItem).displayOrder ?? 999
        : (b.data as ScheduledCollectionData).displayOrder ?? 999;
      return orderA - orderB;
    });
  })();

  const getUnifiedId = (item: UnifiedItem) =>
    item.type === 'always-on' ? item.data.id : (item.data as ScheduledCollectionData).documentId;

  const handleUnifiedDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const newList = [...unifiedList];
    const [moved] = newList.splice(result.source.index, 1);
    newList.splice(result.destination.index, 0, moved);

    // Build unified order data
    const orderData = newList.map((item, i) => ({
      id: item.type === 'always-on'
        ? (item.data as CollectionItem).id
        : (item.data as ScheduledCollectionData).documentId,
      type: item.type as 'always-on' | 'scheduled',
      displayOrder: i,
    }));

    // Optimistically update local state with new displayOrder values
    setCollections(
      newList
        .map((item, i) => ({ item, displayOrder: i }))
        .filter(({ item }) => item.type === 'always-on')
        .map(({ item, displayOrder }) => ({ ...(item.data as CollectionItem), displayOrder }))
    );
    setScheduledCollections(prev =>
      prev.map(sc => {
        const match = orderData.find(o => o.type === 'scheduled' && o.id === sc.documentId);
        return match ? { ...sc, displayOrder: match.displayOrder } : sc;
      })
    );

    setSaving(true);
    try {
      await reorderUnified(storeName, menuType, orderData);
    } catch (error) {
      await loadData();
      alert(`Failed to reorder: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // Render
  // ==========================================

  if (loading) {
    return (
      <div className="home-collections-editor">
        <div className="hce-loading">
          <div className="hce-loading-spinner" />
          <p>Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-collections-editor">
      <h2>Home Collections - {displayName}</h2>
      <p className="hce-subtitle">
        Configure product collections for the HOME page. Collections can be always-on or scheduled for specific dates.
      </p>

      {/* Create button / form */}
      {showCreateForm ? (
        <div className="hce-schedule-form" style={{ marginBottom: 24 }}>
          <h3>New Collection</h3>

          <div className="hce-schedule-field">
            <label>Collection Name</label>
            <input
              className="hce-name-input"
              type="text"
              placeholder="e.g., Staff Picks"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
          </div>

          <div className="hce-schedule-field">
            <label>Products</label>
            <ProductPicker
              products={allProducts}
              productMap={productMap}
              selectedProductIds={schedProductIds}
              onAdd={(id) => setSchedProductIds(prev => [...prev, id])}
              onRemove={(id) => setSchedProductIds(prev => prev.filter(p => p !== id))}
              onAddMultiple={(ids) => setSchedProductIds(prev => [...prev, ...ids.filter(i => !prev.includes(i))])}
              onRemoveMultiple={(ids) => setSchedProductIds(prev => prev.filter(p => !ids.includes(p)))}
              onClearAll={() => setSchedProductIds([])}
              onReorder={(ids) => setSchedProductIds(ids)}
              label="Selected Products"
            />
          </div>

          <label className="hce-shuffle-checkbox">
            <input
              type="checkbox"
              checked={schedShuffleProducts}
              onChange={() => setSchedShuffleProducts(!schedShuffleProducts)}
              disabled={saving}
            />
            Shuffle product order for customers
          </label>

          {renderDealImport(() => {
            handleImportDealProductsScheduled();
          })}

          {/* Scheduling section */}
          <div className="hce-card-schedule-section">
            <div className="hce-schedule-divider"><span>Schedule</span></div>

            <div className="hce-schedule-toggle-row">
              <button
                type="button"
                className="hce-toggle-btn"
                onClick={() => { setCreateScheduled(!createScheduled); if (!createScheduled) setCreateRecurring(false); }}
              >
                <span className={`hce-toggle-track${createScheduled ? ' active' : ''}`}>
                  <span className="hce-toggle-knob" />
                </span>
                <span className="hce-toggle-label">Scheduled</span>
              </button>
              <button
                type="button"
                className="hce-toggle-btn"
                onClick={() => { setCreateRecurring(!createRecurring); if (!createRecurring) setCreateScheduled(false); }}
              >
                <span className={`hce-toggle-track${createRecurring ? ' active' : ''}`}>
                  <span className="hce-toggle-knob" />
                </span>
                <span className="hce-toggle-label">Recurring</span>
              </button>
              {!createScheduled && !createRecurring && (
                <span className="hce-toggle-hint">Toggle to set a schedule or recurring pattern</span>
              )}
            </div>

            {isBrowserTimzoneDifferent(storeName) && (createScheduled || createRecurring) && (
              <div className="hce-timezone-warning">
                Your browser timezone differs from the store timezone ({getTimezoneAbbreviation(storeName)}).
                All times below are in store local time.
              </div>
            )}

            {createScheduled && renderSchedulingInputs(false)}
            {createRecurring && renderRecurringInputs(false)}
          </div>

          <div className="hce-schedule-actions">
            <button
              className="hce-schedule-cancel"
              onClick={() => { setShowCreateForm(false); setNewName(''); setCreateScheduled(false); resetScheduleFields(); }}
            >
              Cancel
            </button>
            <button
              className="hce-schedule-save"
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
            >
              {saving ? 'Creating...' : 'Create Collection'}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="hce-create-btn"
          onClick={() => setShowCreateForm(true)}
          disabled={saving}
        >
          + New Collection
        </button>
      )}

      {/* Empty state */}
      {collections.length === 0 && activeScheduled.length === 0 && !showCreateForm && (
        <div className="hce-empty-state">
          <p>No collections configured</p>
          <small>Click "New Collection" to create your first custom HOME row.</small>
        </div>
      )}

      {/* Unified collections list */}
      <DragDropContext onDragEnd={handleUnifiedDragEnd}>
        <Droppable droppableId="unified-collections-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
      {unifiedList.map((item, index) => {
        const itemId = getUnifiedId(item);
        const anyExpanded = expandedCollectionId !== null || expandedScheduledId !== null;

        if (item.type === 'always-on') {
          const col = item.data as CollectionItem;
          const isExpanded = expandedCollectionId === col.id;
          const isRenaming = renamingId === col.id;

          return (
            <Draggable key={itemId} draggableId={itemId} index={index} isDragDisabled={saving || anyExpanded}>
              {(dragProvided, snapshot) => (
            <div
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              className={`hce-collection-card${col.isActive ? '' : ' inactive'}${snapshot.isDragging ? ' dragging' : ''}`}
            >
              <div className="hce-card-header">
                <div className="hce-card-header-left">
                  {unifiedList.length > 1 && (
                    <span className="hce-collection-drag-handle" {...dragProvided.dragHandleProps} title="Drag to reorder">⠿</span>
                  )}
                  {isRenaming ? (
                    <div className="hce-rename-form">
                      <input
                        className="hce-rename-input"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRename(col);
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        autoFocus
                      />
                      <button
                        className="hce-create-submit"
                        style={{ padding: '4px 10px', fontSize: '12px' }}
                        onClick={() => handleRename(col)}
                        disabled={saving}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="hce-card-name">{col.name}</span>
                      <span className="hce-card-count">{col.productIds.length} product{col.productIds.length !== 1 ? 's' : ''}</span>
                      <span className="hce-badge always-on">Always On</span>
                    </>
                  )}
                </div>
                <div className="hce-card-actions">
                  <button onClick={() => handleExpandAlwaysOn(col.id)}>
                    {isExpanded ? 'Close' : 'Edit'}
                  </button>
                  <button
                    onClick={() => { setRenamingId(col.id); setRenameValue(col.name); }}
                    disabled={saving}
                  >
                    Rename
                  </button>
                  <button
                    className="hce-toggle-btn"
                    onClick={() => handleToggleActive(col)}
                    disabled={saving}
                    title={col.isActive ? 'Click to deactivate' : 'Click to activate'}
                  >
                    <span className={`hce-toggle-track${col.isActive ? ' active' : ''}`}>
                      <span className="hce-toggle-knob" />
                    </span>
                    <span className="hce-toggle-label">{col.isActive ? 'Active' : 'Inactive'}</span>
                  </button>
                  <button
                    className="delete"
                    onClick={() => handleDelete(col)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Expanded edit view */}
              {isExpanded && (
                <div className="hce-expanded-edit">
                  <div className="hce-picker-header">
                    <h4>Products in "{col.name}"</h4>
                  </div>

                  <ProductPicker
                    products={allProducts}
                    productMap={productMap}
                    selectedProductIds={col.productIds}
                    onAdd={(productId) => handleAddProduct(col, productId)}
                    onRemove={(productId) => handleRemoveProduct(col, productId)}
                    onAddMultiple={(productIds) => handleAddMultipleProducts(col, productIds)}
                    onRemoveMultiple={(productIds) => handleRemoveMultipleProducts(col, productIds)}
                    onClearAll={() => handleClearAllProducts(col)}
                    onReorder={(ids) => handleReorderProducts(col, ids)}
                    label="Selected Products"
                  />

                  <label className="hce-shuffle-checkbox">
                    <input
                      type="checkbox"
                      checked={col.shuffleProducts ?? false}
                      onChange={() => handleToggleShuffle(col)}
                      disabled={saving}
                    />
                    Shuffle product order for customers
                  </label>

                  {renderDealImport(() => handleImportDealProducts(col))}

                  {/* Scheduling section */}
                  <div className="hce-card-schedule-section">
                    <div className="hce-schedule-divider"><span>Schedule</span></div>

                    <div className="hce-schedule-toggle-row">
                      <button
                        type="button"
                        className="hce-toggle-btn"
                        onClick={() => { setIsScheduledToggle(!isScheduledToggle); if (!isScheduledToggle) setCreateRecurring(false); }}
                      >
                        <span className={`hce-toggle-track${isScheduledToggle ? ' active' : ''}`}>
                          <span className="hce-toggle-knob" />
                        </span>
                        <span className="hce-toggle-label">Scheduled</span>
                      </button>
                      <button
                        type="button"
                        className="hce-toggle-btn"
                        onClick={() => { setCreateRecurring(!createRecurring); if (!createRecurring) setIsScheduledToggle(false); }}
                      >
                        <span className={`hce-toggle-track${createRecurring ? ' active' : ''}`}>
                          <span className="hce-toggle-knob" />
                        </span>
                        <span className="hce-toggle-label">Recurring</span>
                      </button>
                      {!isScheduledToggle && !createRecurring && (
                        <span className="hce-toggle-hint">Toggle on to set a time window for this collection</span>
                      )}
                    </div>

                    {isBrowserTimzoneDifferent(storeName) && (isScheduledToggle || createRecurring) && (
                      <div className="hce-timezone-warning">
                        Your browser timezone differs from the store timezone ({getTimezoneAbbreviation(storeName)}).
                        All times below are in store local time.
                      </div>
                    )}

                    {isScheduledToggle && renderSchedulingInputs(false)}
                    {createRecurring && renderRecurringInputs(false)}

                    {(isScheduledToggle || createRecurring) && (
                      <button
                        className="hce-schedule-save"
                        onClick={() => handleConvertToScheduled(col)}
                        disabled={saving}
                        style={{ marginTop: 8 }}
                      >
                        {saving ? 'Converting...' : createRecurring ? 'Convert to Recurring' : 'Convert to Scheduled'}
                      </button>
                    )}
                  </div>

                  <button
                    className="hce-picker-done"
                    onClick={() => handleExpandAlwaysOn(null)}
                    style={{ marginTop: 16 }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
              )}
            </Draggable>
          );
        } else {
          const col = item.data as ScheduledCollectionData;
          const isExpanded = expandedScheduledId === col.documentId;
          const isRecurring = !!col.isRecurring;
          const statusLabel = isRecurring ? 'Recurring' : (col.status === 'current' ? 'Active Now' : 'Upcoming');
          const statusClass = isRecurring ? 'recurring' : (col.status === 'current' ? 'active' : 'upcoming');

          return (
            <Draggable key={itemId} draggableId={itemId} index={index} isDragDisabled={saving || anyExpanded}>
              {(dragProvided, snapshot) => (
            <div
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              className={`hce-collection-card scheduled${snapshot.isDragging ? ' dragging' : ''}`}
            >
              <div className="hce-card-header">
                <div className="hce-card-header-left">
                  {unifiedList.length > 1 && (
                    <span className="hce-collection-drag-handle" {...dragProvided.dragHandleProps} title="Drag to reorder">⠿</span>
                  )}
                  <span className="hce-card-name">{col.name}</span>
                  <span className="hce-card-count">{col.productIds.length} product{col.productIds.length !== 1 ? 's' : ''}</span>
                  <span className={`hce-badge ${statusClass}`}>{statusLabel}</span>
                  <span className="hce-date-range">
                    {isRecurring
                      ? `${DAY_NAMES[col.recurringStartDay!]} ${format12h(col.recurringStartTime!)} → ${DAY_NAMES[col.recurringEndDay!]} ${format12h(col.recurringEndTime!)}`
                      : formatDateRange(col.startDateTime, col.endDateTime, storeName)}
                  </span>
                  {isRecurring && col.status === 'current' && (
                    <span className="hce-badge active">Active Now</span>
                  )}
                </div>
                <div className="hce-card-actions">
                  <button onClick={() => handleExpandScheduled(col)}>
                    {isExpanded ? 'Close' : 'Edit'}
                  </button>
                  <button onClick={() => handleDuplicateScheduled(col)} disabled={saving}>
                    Duplicate
                  </button>
                  <button
                    className="delete"
                    onClick={() => handleDeleteScheduled(col)}
                    disabled={saving}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Expanded edit view */}
              {isExpanded && (
                <div className="hce-expanded-edit">
                  <div className="hce-schedule-field">
                    <label>Collection Name</label>
                    <input
                      className="hce-name-input"
                      type="text"
                      value={schedName}
                      onChange={e => setSchedName(e.target.value)}
                    />
                  </div>

                  <div className="hce-schedule-field">
                    <label>Products</label>
                    <ProductPicker
                      products={allProducts}
                      productMap={productMap}
                      selectedProductIds={schedProductIds}
                      onAdd={(id) => setSchedProductIds(prev => [...prev, id])}
                      onRemove={(id) => setSchedProductIds(prev => prev.filter(p => p !== id))}
                      onAddMultiple={(ids) => setSchedProductIds(prev => [...prev, ...ids.filter(id => !prev.includes(id))])}
                      onRemoveMultiple={(ids) => setSchedProductIds(prev => prev.filter(p => !ids.includes(p)))}
                      onClearAll={() => setSchedProductIds([])}
                      onReorder={(ids) => setSchedProductIds(ids)}
                      label="Selected Products"
                    />
                  </div>

                  <label className="hce-shuffle-checkbox">
                    <input
                      type="checkbox"
                      checked={schedShuffleProducts}
                      onChange={() => setSchedShuffleProducts(!schedShuffleProducts)}
                      disabled={schedSaving}
                    />
                    Shuffle product order for customers
                  </label>

                  {renderDealImport(handleImportDealProductsScheduled)}

                  {/* Scheduling section */}
                  <div className="hce-card-schedule-section">
                    <div className="hce-schedule-divider"><span>Schedule</span></div>

                    <div className="hce-schedule-toggle-row">
                      <button
                        type="button"
                        className="hce-toggle-btn"
                        onClick={() => { setIsScheduledToggle(!isScheduledToggle); if (!isScheduledToggle) setCreateRecurring(false); }}
                      >
                        <span className={`hce-toggle-track${isScheduledToggle ? ' active' : ''}`}>
                          <span className="hce-toggle-knob" />
                        </span>
                        <span className="hce-toggle-label">Scheduled</span>
                      </button>
                      <button
                        type="button"
                        className="hce-toggle-btn"
                        onClick={() => { setCreateRecurring(!createRecurring); if (!createRecurring) setIsScheduledToggle(false); }}
                      >
                        <span className={`hce-toggle-track${createRecurring ? ' active' : ''}`}>
                          <span className="hce-toggle-knob" />
                        </span>
                        <span className="hce-toggle-label">Recurring</span>
                      </button>
                      {!isScheduledToggle && !createRecurring && (
                        <span className="hce-toggle-hint">Toggle off to make this collection always-on</span>
                      )}
                    </div>

                    {isBrowserTimzoneDifferent(storeName) && (isScheduledToggle || createRecurring) && (
                      <div className="hce-timezone-warning">
                        Your browser timezone differs from the store timezone ({getTimezoneAbbreviation(storeName)}).
                      </div>
                    )}

                    {isScheduledToggle && renderSchedulingInputs(false)}
                    {createRecurring && renderRecurringInputs(false)}
                  </div>

                  <div className="hce-schedule-actions">
                    <button
                    className="hce-schedule-cancel"
                    onClick={() => handleExpandScheduled(null)}
                    disabled={schedSaving}
                  >
                    Cancel
                  </button>
                  {(isScheduledToggle || createRecurring) ? (
                    <button
                      className="hce-schedule-save"
                      onClick={handleSaveScheduledEdit}
                      disabled={schedSaving}
                    >
                      {schedSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  ) : (
                    <button
                      className="hce-schedule-save"
                      onClick={() => handleConvertToAlwaysOn(col)}
                      disabled={saving}
                    >
                      {saving ? 'Converting...' : 'Make Always On'}
                    </button>
                  )}
                </div>
              </div>
            )}
            </div>
              )}
            </Draggable>
          );
        }
      })}
      {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Expired scheduled collections */}
      {expiredScheduled.length > 0 && (
        <div className="hce-expired-section">
          <details>
            <summary className="hce-expired-summary">
              Expired ({expiredScheduled.length})
            </summary>
            {expiredScheduled.map(col => (
              <div key={col.documentId} className="hce-collection-card expired">
                <div className="hce-card-header">
                  <div className="hce-card-header-left">
                    <span className="hce-card-name">{col.name}</span>
                    <span className="hce-card-count">{col.productIds.length} product{col.productIds.length !== 1 ? 's' : ''}</span>
                    <span className="hce-badge expired">Expired</span>
                    <span className="hce-date-range">
                      {formatDateRange(col.startDateTime, col.endDateTime, storeName)}
                    </span>
                  </div>
                  <div className="hce-card-actions">
                    <button onClick={() => handleDuplicateScheduled(col)} disabled={saving}>
                      Duplicate
                    </button>
                    <button
                      className="delete"
                      onClick={() => handleDeleteScheduled(col)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </details>
        </div>
      )}
    </div>
  );
};

export default HomeCollectionsEditor;
